// src/pages/Features.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import DataTable from '../components/DataTable';
import {
    SparklesIcon,
    AdjustmentsHorizontalIcon,
    CheckCircleIcon,
    XCircleIcon,
    ArrowPathIcon,
    BuildingOfficeIcon
} from '@heroicons/react/24/outline';

// Tier hierarchy for feature access
const TIER_HIERARCHY = ['BASIC', 'PRO', 'ENTERPRISE'];

// Features included in each tier
const TIER_FEATURES = {
    BASIC: [
        'tender_search', 'basic_export', 'email_notifications',
        '5_saved_searches', 'document_preview', 'basic_analytics', 'single_user'
    ],
    PRO: [
        'tender_search', 'basic_export', 'email_notifications',
        '5_saved_searches', 'document_preview', 'basic_analytics', 'single_user',
        'advanced_analytics', 'api_access', 'bulk_export',
        'unlimited_saved_searches', 'team_collaboration', 'priority_support', 'custom_alerts'
    ],
    ENTERPRISE: [
        'tender_search', 'basic_export', 'email_notifications',
        '5_saved_searches', 'document_preview', 'basic_analytics', 'single_user',
        'advanced_analytics', 'api_access', 'bulk_export',
        'unlimited_saved_searches', 'team_collaboration', 'priority_support', 'custom_alerts',
        'whitelabel', 'custom_integrations', 'dedicated_support',
        'sla_guarantee', 'audit_logs', 'on_premise'
    ]
};

const getTierBadgeColor = (tier) => {
    switch (tier) {
        case 'BASIC': return 'bg-slate-100 text-slate-700';
        case 'PRO': return 'bg-blue-100 text-blue-700';
        case 'ENTERPRISE': return 'bg-purple-100 text-purple-700';
        default: return 'bg-slate-100 text-slate-700';
    }
};

const getModuleBadgeColor = (module) => {
    const colors = {
        core: 'bg-emerald-100 text-emerald-700',
        export: 'bg-cyan-100 text-cyan-700',
        notifications: 'bg-yellow-100 text-yellow-700',
        search: 'bg-blue-100 text-blue-700',
        documents: 'bg-orange-100 text-orange-700',
        analytics: 'bg-purple-100 text-purple-700',
        users: 'bg-pink-100 text-pink-700',
        integrations: 'bg-indigo-100 text-indigo-700',
        support: 'bg-green-100 text-green-700',
        branding: 'bg-red-100 text-red-700',
        compliance: 'bg-amber-100 text-amber-700',
        infrastructure: 'bg-slate-100 text-slate-700'
    };
    return colors[module] || 'bg-slate-100 text-slate-700';
};

const Features = () => {
    const { executeQuery, queries, loading } = useGraphQL();
    const [activeTab, setActiveTab] = useState('catalog');
    const [catalog, setCatalog] = useState([]);
    const [overrides, setOverrides] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setError(null);
        try {
            // Load feature catalog
            const catalogData = await executeQuery(queries.getFeatureCatalog());
            setCatalog(catalogData?.featureCatalog || []);

            // Load all overrides
            const overridesData = await executeQuery(queries.getAllFeatureOverrides());
            setOverrides(overridesData?.allFeatureOverrides || []);

            // Load companies for name lookup
            const companiesData = await executeQuery(queries.getCompanies(), { limit: 1000, offset: 0 });
            setCompanies(companiesData?.adminCompanies || []);
        } catch (err) {
            console.error('Failed to load feature data:', err);
            setError(err.message);
        }
    };

    const getCompanyName = (companyId) => {
        const company = companies.find(c => c.id === companyId);
        return company?.name || companyId?.slice(0, 8) + '...';
    };

    const getFeatureName = (featureId) => {
        const feature = catalog.find(f => f.id === featureId);
        return feature?.name || featureId;
    };

    // Group catalog by module
    const catalogByModule = catalog.reduce((acc, feature) => {
        const module = feature.module || 'other';
        if (!acc[module]) acc[module] = [];
        acc[module].push(feature);
        return acc;
    }, {});

    // Group overrides by company
    const overridesByCompany = overrides.reduce((acc, override) => {
        if (!acc[override.companyId]) acc[override.companyId] = [];
        acc[override.companyId].push(override);
        return acc;
    }, {});

    // Stats
    const totalGrants = overrides.filter(o => o.enabled).length;
    const totalRevokes = overrides.filter(o => !o.enabled).length;
    const companiesWithOverrides = Object.keys(overridesByCompany).length;

    const catalogColumns = [
        {
            key: 'name',
            title: 'Feature',
            sortable: true,
            render: (name, feature) => (
                <div>
                    <div className="font-medium text-slate-900">{name}</div>
                    <div className="text-sm text-slate-400">{feature.id}</div>
                </div>
            )
        },
        {
            key: 'description',
            title: 'Description',
            render: (desc) => (
                <span className="text-slate-600 text-sm">{desc || '-'}</span>
            )
        },
        {
            key: 'minTier',
            title: 'Min Tier',
            sortable: true,
            render: (tier) => (
                <span className={`px-2 py-1 rounded text-xs font-medium ${getTierBadgeColor(tier)}`}>
                    {tier}
                </span>
            )
        },
        {
            key: 'module',
            title: 'Module',
            sortable: true,
            render: (module) => (
                <span className={`px-2 py-1 rounded text-xs font-medium ${getModuleBadgeColor(module)}`}>
                    {module || 'other'}
                </span>
            )
        },
        {
            key: 'tiers',
            title: 'Available In',
            render: (_, feature) => (
                <div className="flex gap-1">
                    {TIER_HIERARCHY.map(tier => {
                        const hasFeature = TIER_FEATURES[tier]?.includes(feature.id);
                        return (
                            <span
                                key={tier}
                                className={`w-6 h-6 rounded flex items-center justify-center text-xs ${
 hasFeature
 ? 'bg-green-100 text-green-700'
 : 'bg-slate-100 text-slate-400'
 }`}
                                title={`${tier}: ${hasFeature ? 'Included' : 'Not included'}`}
                            >
                                {tier[0]}
                            </span>
                        );
                    })}
                </div>
            )
        }
    ];

    const overrideColumns = [
        {
            key: 'companyId',
            title: 'Company',
            sortable: true,
            render: (companyId) => (
                <div className="flex items-center space-x-2">
                    <BuildingOfficeIcon className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-900">{getCompanyName(companyId)}</span>
                </div>
            )
        },
        {
            key: 'featureId',
            title: 'Feature',
            sortable: true,
            render: (featureId) => (
                <div>
                    <div className="font-medium text-slate-900">{getFeatureName(featureId)}</div>
                    <div className="text-sm text-slate-400">{featureId}</div>
                </div>
            )
        },
        {
            key: 'enabled',
            title: 'Type',
            sortable: true,
            render: (enabled) => (
                enabled ? (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Grant
                    </span>
                ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700">
                        <XCircleIcon className="h-3 w-3 mr-1" />
                        Revoke
                    </span>
                )
            )
        },
        {
            key: 'reason',
            title: 'Reason',
            render: (reason) => (
                <span className="text-slate-600 text-sm max-w-xs truncate block" title={reason}>
                    {reason || '-'}
                </span>
            )
        },
        {
            key: 'createdBy',
            title: 'Created By',
            render: (createdBy) => (
                <span className="text-slate-500 text-sm">{createdBy || 'System'}</span>
            )
        },
        {
            key: 'createdAt',
            title: 'Created',
            sortable: true,
            render: (createdAt) => (
                <span className="text-slate-500 text-sm">
                    {createdAt ? new Date(createdAt).toLocaleDateString() : '-'}
                </span>
            )
        }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-slate-900">Feature Access Control</h1>
                <button
                    onClick={loadData}
                    className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 flex items-center space-x-2"
                >
                    <ArrowPathIcon className="h-4 w-4" />
                    <span>Refresh</span>
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="surface-card p-4">
                    <div className="flex items-center space-x-3">
                        <SparklesIcon className="h-8 w-8 text-primary" />
                        <div>
                            <div className="text-2xl font-bold text-slate-900">{catalog.length}</div>
                            <div className="text-sm text-slate-500">Total Features</div>
                        </div>
                    </div>
                </div>
                <div className="surface-card p-4">
                    <div className="flex items-center space-x-3">
                        <AdjustmentsHorizontalIcon className="h-8 w-8 text-amber-500" />
                        <div>
                            <div className="text-2xl font-bold text-slate-900">{overrides.length}</div>
                            <div className="text-sm text-slate-500">Total Overrides</div>
                        </div>
                    </div>
                </div>
                <div className="surface-card p-4">
                    <div className="flex items-center space-x-3">
                        <CheckCircleIcon className="h-8 w-8 text-green-600" />
                        <div>
                            <div className="text-2xl font-bold text-slate-900">{totalGrants}</div>
                            <div className="text-sm text-slate-500">Feature Grants</div>
                        </div>
                    </div>
                </div>
                <div className="surface-card p-4">
                    <div className="flex items-center space-x-3">
                        <XCircleIcon className="h-8 w-8 text-orange-500" />
                        <div>
                            <div className="text-2xl font-bold text-slate-900">{totalRevokes}</div>
                            <div className="text-sm text-slate-500">Feature Revokes</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-700">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('catalog')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm ${
 activeTab === 'catalog'
 ? 'border-primary text-primary'
 : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
 }`}
                    >
                        <SparklesIcon className="h-5 w-5 inline mr-2" />
                        Feature Catalog ({catalog.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('overrides')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm ${
 activeTab === 'overrides'
 ? 'border-primary text-primary'
 : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
 }`}
                    >
                        <AdjustmentsHorizontalIcon className="h-5 w-5 inline mr-2" />
                        Active Overrides ({overrides.length})
                    </button>
                </nav>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            )}

            {/* Tab Content */}
            {activeTab === 'catalog' && (
                <div className="space-y-6">
                    {/* Module summary */}
                    <div className="surface-card p-4">
                        <h3 className="text-sm font-medium text-slate-600 mb-3">Features by Module</h3>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(catalogByModule).map(([module, features]) => (
                                <span
                                    key={module}
                                    className={`px-3 py-1 rounded text-sm ${getModuleBadgeColor(module)}`}
                                >
                                    {module}: {features.length}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Feature catalog table */}
                    <DataTable
                        columns={catalogColumns}
                        data={catalog}
                        loading={loading}
                    />

                    {/* Tier comparison */}
                    <div className="surface-card p-4">
                        <h3 className="text-sm font-medium text-slate-600 mb-3">Tier Comparison</h3>
                        <div className="grid grid-cols-3 gap-4">
                            {TIER_HIERARCHY.map(tier => (
                                <div key={tier} className="space-y-2">
                                    <div className={`inline-block px-3 py-1 rounded text-sm font-medium ${getTierBadgeColor(tier)}`}>
                                        {tier}
                                    </div>
                                    <div className="text-2xl font-bold text-slate-900">
                                        {TIER_FEATURES[tier]?.length || 0}
                                    </div>
                                    <div className="text-sm text-slate-500">features</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'overrides' && (
                <div className="space-y-6">
                    {/* Overrides summary */}
                    {companiesWithOverrides > 0 && (
                        <div className="surface-card p-4">
                            <h3 className="text-sm font-medium text-slate-600 mb-3">
                                {companiesWithOverrides} Companies with Overrides
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(overridesByCompany).map(([companyId, companyOverrides]) => {
                                    const grants = companyOverrides.filter(o => o.enabled).length;
                                    const revokes = companyOverrides.filter(o => !o.enabled).length;
                                    return (
                                        <span
                                            key={companyId}
                                            className="px-3 py-1 rounded text-sm bg-slate-100 text-slate-700"
                                            title={`${grants} grants, ${revokes} revokes`}
                                        >
                                            {getCompanyName(companyId)}
                                            <span className="ml-2 text-green-600">+{grants}</span>
                                            <span className="ml-1 text-orange-600">-{revokes}</span>
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {overrides.length === 0 ? (
                        <div className="surface-card p-8 text-center">
                            <AdjustmentsHorizontalIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-slate-900 mb-2">No Feature Overrides</h3>
                            <p className="text-slate-500">
                                All companies are using their tier&apos;s default feature set.
                                <br />
                                To add overrides, go to a company&apos;s detail page and manage their features.
                            </p>
                        </div>
                    ) : (
                        <DataTable
                            columns={overrideColumns}
                            data={overrides}
                            loading={loading}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default Features;
